"use client";

import { useState, useRef, useEffect } from "react";
import { ChevronLeft, Minus, Plus, GripVertical } from "lucide-react";
import { Button } from "@/shadcn-components/ui/button";
import { Card, CardContent } from "@/shadcn-components/ui/card";
import { Badge } from "@/shadcn-components/ui/badge";
import { useSettingsStore } from "@/lib/settings";
import { QUOTE_COLUMNS } from "@/lib/settings/default-data";

interface ColumnsScreenProps {
  onBack: () => void;
  isDesktop?: boolean;
}

export default function ColumnsScreen({ onBack, isDesktop = false }: ColumnsScreenProps) {
  const columns = useSettingsStore((state) => state.settings.columns);
  const updateSetting = useSettingsStore((state) => state.updateSetting);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [activeDeleteIndex, setActiveDeleteIndex] = useState<number | null>(null);

  // Get available columns (not in the active list)
  const availableColumns = QUOTE_COLUMNS.filter(col => !columns.includes(col));

  const handleRemoveColumn = (column: string) => {
    const newColumns = columns.filter(col => col !== column);
    updateSetting('columns', newColumns);
    setActiveDeleteIndex(null);
  };

  const handleAddColumn = (column: string) => {
    const newColumns = [...columns, column];
    updateSetting('columns', newColumns);
  };

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>, index: number) => {
    // If delete is shown for this item, prevent drag and hide delete
    if (activeDeleteIndex === index) {
      e.preventDefault();
      setActiveDeleteIndex(null);
      return;
    }
    
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>, dropIndex: number) => {
    e.preventDefault();

    if (draggedIndex === null || draggedIndex === dropIndex) {
      return;
    }

    const newColumns = [...columns];
    const draggedItem = newColumns[draggedIndex];

    // Remove dragged item
    newColumns.splice(draggedIndex, 1);

    // Insert at new position
    const insertIndex = draggedIndex < dropIndex ? dropIndex - 1 : dropIndex;
    newColumns.splice(insertIndex, 0, draggedItem);

    updateSetting('columns', newColumns);
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleMinusClick = (index: number) => {
    setActiveDeleteIndex(activeDeleteIndex === index ? null : index);
  };

  // Click outside to hide delete
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.delete-button') && 
          !target.closest('.minus-button') && 
          !target.closest('.burger-icon')) {
        setActiveDeleteIndex(null);
      }
    };

    if (activeDeleteIndex !== null) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [activeDeleteIndex]);

  return (
    <div className="h-full bg-background text-foreground flex flex-col">
      {/* Header according to developer-guidelines */}
      <div className={`flex items-center justify-between ${isDesktop ? 'px-3 py-2 h-10' : 'px-4 py-3 h-14'} bg-background border-b border-border`}>
        <Button 
          variant="ghost" 
          size="icon" 
          onClick={onBack}
          className={`${isDesktop ? 'h-6 w-6' : 'h-10 w-10'}`}
        >
          <ChevronLeft className={`${isDesktop ? 'h-3 w-3' : 'h-5 w-5'}`} />
        </Button>
        <h1 className={`${isDesktop ? 'text-xs' : 'text-lg'} font-medium text-foreground`}>Columns</h1>
        {/* Empty div for spacing balance */}
        <div className={`${isDesktop ? 'w-6' : 'w-10'}`}></div>
      </div>

      {/* Column Options */}
      <div className={`flex-1 overflow-y-auto ${isDesktop ? 'p-3' : 'p-4'} space-y-4`}>
        {/* Active Columns Card */}
        {columns.length > 0 && (
          <Card>
            <CardContent className="p-0">
              {columns.map((column, index) => (
                <div
                  key={column}
                  className="relative overflow-hidden"
                >
                  <div
                    className={`relative bg-background transition-all duration-300 ease-out flex items-center justify-between ${isDesktop ? 'p-2' : 'p-3'}
                      ${draggedIndex === index ? "opacity-50" : ""}
                      ${
                        dragOverIndex === index && draggedIndex !== index
                          ? "border-t-2 border-t-primary"
                          : ""
                      }
                      ${activeDeleteIndex === index ? "-translate-x-20" : "translate-x-0"}
                      ${index !== columns.length - 1 ? "border-b border-border" : ""}`}
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDrop={(e) => handleDrop(e, index)}
                  >
                    <div className="flex items-center gap-3 flex-1">
                      <Button
                        variant="destructive"
                        size="icon"
                        onClick={() => handleMinusClick(index)}
                        className={`minus-button ${isDesktop ? 'h-6 w-6' : 'h-8 w-8'} rounded-full`}
                      >
                        <Minus className={`${isDesktop ? 'h-3 w-3' : 'h-4 w-4'}`} />
                      </Button>
                      <span className={`text-foreground ${isDesktop ? 'text-sm' : 'text-base'} font-medium`}>{column}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div
                        draggable={activeDeleteIndex !== index}
                        onDragStart={(e) => handleDragStart(e, index)}
                        onDragEnd={handleDragEnd}
                        onClick={() => {
                          if (activeDeleteIndex === index) {
                            setActiveDeleteIndex(null);
                          }
                        }}
                        className="cursor-move p-1 hover:bg-accent rounded burger-icon"
                      >
                        <GripVertical className={`${isDesktop ? 'h-4 w-4' : 'h-5 w-5'} text-muted-foreground`} />
                      </div>
                    </div>
                  </div>
                  
                  {/* Delete button */}
                  <Button
                    variant="destructive"
                    onClick={() => handleRemoveColumn(column)}
                    className={`delete-button absolute right-0 top-0 h-full w-20 rounded-none transition-opacity duration-200 ${
                      activeDeleteIndex === index ? 'opacity-100' : 'opacity-0 pointer-events-none'
                    }`}
                  >
                    <span className={`${isDesktop ? 'text-xs' : 'text-sm'} font-medium`}>Delete</span>
                  </Button>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Available Columns Card */}
        {availableColumns.length > 0 && (
          <Card>
            <CardContent className="p-0">
              {availableColumns.map((column, index) => (
                <div
                  key={column}
                  className={`flex items-center justify-between ${isDesktop ? 'p-2' : 'p-3'} ${
                    index !== availableColumns.length - 1 ? "border-b border-border" : ""
                  }`}
                >
                  <div className="flex items-center gap-3 flex-1">
                    <Button
                      variant="default"
                      size="icon"
                      onClick={() => handleAddColumn(column)}
                      className={`${isDesktop ? 'h-6 w-6' : 'h-8 w-8'} rounded-full bg-green-600 hover:bg-green-700`}
                    >
                      <Plus className={`${isDesktop ? 'h-3 w-3' : 'h-4 w-4'}`} />
                    </Button>
                    <span className={`text-foreground ${isDesktop ? 'text-sm' : 'text-base'} font-medium`}>{column}</span>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Empty State */}
        {columns.length === 0 && availableColumns.length === 0 && (
          <div className="flex items-center justify-center h-full">
            <div className="text-center text-muted-foreground">
              <p className="text-lg mb-2">No columns available</p>
              <p className="text-sm">All columns are already active</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}